import { useCallback, useEffect, useRef, useState } from 'react'
import { getAllHacks } from '@domain/features/pd-hacks/pd-hacks-feature'
import type { HackSymptom } from '@domain/models/pd-hacks'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { getContentRefreshPolicy } from '@presentation/hooks/content-refresh-policy'
import { useAutoRefreshOnForeground } from '@presentation/hooks/useAutoRefreshOnForeground'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'

export function useHacks(pdHacksCategoryId: string | null = null) {
  const ssrHacks = useSsrPageData()?.hacksList?.hacks
  const { user, loading: authLoading } = useAuthUser()
  const [hacks, setHacks] = useState<HackSymptom[]>(ssrHacks ?? [])
  const [loading, setLoading] = useState(!ssrHacks)
  const [error, setError] = useState<string | null>(null)
  const normalizedCategoryId = pdHacksCategoryId?.trim() ?? ''
  const requestIdRef = useRef(0)
  const refreshPolicy = getContentRefreshPolicy('list')

  const {
    triggerRefresh,
    lastSuccessAt,
    isRefreshing,
  } = useAutoRefreshOnForeground({
    enabled: !authLoading,
    refresh: async ({ reason }) => {
      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      const showInitialLoading = reason === 'mount' && hacks.length === 0

      if (showInitialLoading) {
        setLoading(true)
      }

      try {
        setError(null)
        const result = await getAllHacks(normalizedCategoryId || undefined)
        if (requestId === requestIdRef.current) {
          setHacks(result)
        }
      } catch (loadError) {
        if (requestId === requestIdRef.current) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load Michel PD hacks.',
          )
        }
        throw loadError
      } finally {
        if (showInitialLoading && requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    },
    staleAfterMs: refreshPolicy.staleAfterMs,
    pollIntervalMs: refreshPolicy.pollIntervalMs,
    minRefreshGapMs: refreshPolicy.minRefreshGapMs,
    maxFailureBackoffMs: refreshPolicy.maxFailureBackoffMs,
  })

  useEffect(() => {
    if (authLoading) {
      return
    }

    void triggerRefresh('mount', { force: true })
  }, [authLoading, normalizedCategoryId, triggerRefresh, user?.uid, user?.isAdmin])

  const refresh = useCallback(async () => {
    await triggerRefresh('manual', { force: true })
  }, [triggerRefresh])

  return {
    hacks,
    loading,
    error,
    refresh,
    lastUpdatedAt: lastSuccessAt,
    isRefreshing,
  }
}
